import { useState, useRef, useEffect } from 'react';
import { Tabs, Spin, message, Button } from 'antd';
import './ApiWorkflowTemplate.scss';
import SearchBar from './components/SearchBar';
import TabItems from './components/TabItems';
import apiClient from '@/utils/apiClient';
import { ApiTemplate, CategoryData, Integration } from './types';
import RequestIntegrationModal from './components/requestIntegrationModal';
import Header from '@/components/Header';
import { AppstoreOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';
import { safeJSONParse } from '@/utils/helper';

const ApiWorkflowTemplate = () => {
	const [activeTab, setActiveTab] = useState('CRM');
	const [searchText, setSearchText] = useState('');
	const PAGE_SIZE = 6;
	const [currentPage, setCurrentPage] = useState(1);
	const [showLeftButton, setShowLeftButton] = useState(false);
	const [showRightButton, setShowRightButton] = useState(true);
	const [integrationModalVisible, setIntegrationModalVisible] = useState(false);
	const [loading, setLoading] = useState(true);
	const [categorizedData, setCategorizedData] = useState<CategoryData[]>([]);
	const tabsContainerRef = useRef<HTMLDivElement>(null);

	// Fetch template from API
	useEffect(() => {
		const fetchTemplates = async () => {
			try {
				setLoading(true);
				const response = await apiClient.request({
					method: 'GET',
					endPoint: '/api/template-workflow/all',
				});

				if (response.success && response.data) {
					const processed = processTemplatesIntoCategories(response.data);
					setCategorizedData(processed);

					// Set default active tab if available
					if (processed.length > 0 && processed[0].category) {
						setActiveTab(processed[0].category);
					}
				} else {
					message.error('Failed to load templates');
				}
			} catch (error) {
				console.error('Error fetching templates:', error);
				message.error('Error loading templates');
			} finally {
				setLoading(false);
			}
		};
		fetchTemplates();
	}, []);

	const processTemplatesIntoCategories = (apiTemplates: ApiTemplate[]): CategoryData[] => {
		const categoryMap = new Map<string, Integration[]>();
		const DEFAULT_CATEGORY = 'General';
		const knownCategories = [
			'All',
			'Auto Repair',
			'Auto Sales & Service',
			'Beauty',
			'Car Dealership',
			'Construction',
			'CRM',
			'Entertainment',
			'Fitness',
			'Food',
			'HVAC',
			'Legal',
			'Marketing',
			'Other Automotive Business',
			'Real Estate',
			'Retail',
			'Support',
			'Technology',
		];
		categoryMap.set('All', []);

		apiTemplates.forEach((template) => {
			const integration: Integration = {
				name: template.name,
				description: template.description || 'Integration tool',
				features:
					typeof template.features === 'string'
						? template.features
						: JSON.stringify(
								template.api.reduce(
									(obj: Record<string, number[]>, api, index) => {
										if (api.name) {
											obj[api.name] = [api.id || index];
										}
										return obj;
									},
									{} as Record<string, number[]>,
								),
							),
				keywords:
					typeof template.businessType === 'string'
						? template.businessType
						: Array.isArray(template.businessType)
							? template.businessType.join(',')
							: template.keywords,
				type: template.type,
				api: template.api,
				instructionLink: template.instructionLink,
				businessType: template.businessType,
			};

			categoryMap.get('All')?.push({ ...integration });

			try {
				let businessTypes: string[] = [];

				if (typeof template.businessType === 'string') {
					if (template.businessType.startsWith('[') && template.businessType.endsWith(']')) {
						businessTypes = safeJSONParse(template.businessType, []);
					} else {
						businessTypes = template.businessType.split(',').map((type) => type.trim());
					}
				} else if (Array.isArray(template.businessType)) {
					businessTypes = template.businessType;
				}

				if (businessTypes.length === 0) {
					const keywords = template.keywords?.split(',').map((k) => k.trim().toLowerCase()) || [];

					let foundCategory = false;
					for (const knownCat of knownCategories) {
						if (knownCat === 'All') {
							continue;
						}
						if (keywords.some((k) => k.includes(knownCat.toLowerCase()))) {
							addToCategory(categoryMap, knownCat, integration);
							foundCategory = true;
						}
					}
					if (!foundCategory) {
						addToCategory(categoryMap, DEFAULT_CATEGORY, integration);
					}
				} else {
					let foundCategory = false;
					for (const businessType of businessTypes) {
						const matchingCategory = knownCategories.find(
							(cat) => cat !== 'All' && cat.toLowerCase() === businessType.toLowerCase(),
						);

						if (matchingCategory) {
							addToCategory(categoryMap, matchingCategory, integration);
							foundCategory = true;
						}
					}
					if (!foundCategory) {
						addToCategory(categoryMap, DEFAULT_CATEGORY, integration);
					}
				}
			} catch (error) {
				console.error('Error processing categories for template:', template.name, error);
				addToCategory(categoryMap, DEFAULT_CATEGORY, integration);
			}
		});

		function addToCategory(
			map: Map<string, Integration[]>,
			category: string,
			integration: Integration,
		) {
			if (!map.has(category)) {
				map.set(category, []);
			}
			map.get(category)?.push({ ...integration });
		}

		// Convert map to array structure
		return Array.from(categoryMap.entries()).map(([category, integrations]) => ({
			category,
			integrations,
		}));
	};
	const categories = categorizedData.map((cat) => cat.category);

	const handleTabChange = (value: string) => {
		setActiveTab(value);
		setCurrentPage(1);
	};
	const handleClearSearch = () => {
		setSearchText('');
	};

	const scrollLeft = () => {
		if (tabsContainerRef.current) {
			tabsContainerRef.current.querySelector('.ant-tabs-nav-list')?.scrollBy({
				left: -200,
				behavior: 'smooth',
			});
		}
	};

	const scrollRight = () => {
		if (tabsContainerRef.current) {
			tabsContainerRef.current.querySelector('.ant-tabs-nav-list')?.scrollBy({
				left: 200,
				behavior: 'smooth',
			});
		}
	};

	useEffect(() => {
		const checkScroll = () => {
			if (!tabsContainerRef.current) {
				return;
			}

			const tabsList = tabsContainerRef.current.querySelector('.ant-tabs-nav-list');
			if (!tabsList) {
				return;
			}

			const { scrollLeft, scrollWidth, clientWidth } = tabsList as HTMLElement;

			setShowLeftButton(scrollLeft > 0);
			setShowRightButton(scrollLeft + clientWidth < scrollWidth);
		};

		checkScroll();

		const tabsList = tabsContainerRef.current?.querySelector('.ant-tabs-nav-list');
		if (tabsList) {
			tabsList.addEventListener('scroll', checkScroll);
		}
		return () => {
			if (tabsList) {
				tabsList.removeEventListener('scroll', checkScroll);
			}
		};
	}, []);

	const currentCategory = categorizedData.find(
		(cat) => cat.category.toLowerCase() === activeTab.toLowerCase(),
	);

	const allIntegrations = categorizedData.reduce<Integration[]>((acc, category) => {
		return [...acc, ...category.integrations];
	}, []);

	const filteredItems = searchText
		? allIntegrations
				.filter((item) => {
					const featuresObj = (() => {
						if (typeof item.features === 'string') {
							return safeJSONParse(item.features, {});
						} else if (Array.isArray(item.features)) {
							return item.features.reduce(
								(obj, feat) => {
									const key = feat.name || feat.feature || '';
									if (key) {
										obj[key] = true;
									}
									return obj;
								},
								{} as Record<string, boolean>,
							);
						} else if (item.features && typeof item.features === 'object') {
							return item.features;
						}
						return {};
					})();

					const featureNames = Object.keys(featuresObj).join(' ');
					const combinedText = `${item.name} ${item.description || ''} ${featureNames}`;
					return combinedText.toLowerCase().includes(searchText.toLowerCase());
				})
				.filter((item, index, self) => index === self.findIndex((t) => t.name === item.name))
		: currentCategory
			? currentCategory.integrations
			: [];

	const totalItems = filteredItems.length;
	const totalPages = Math.ceil(totalItems / PAGE_SIZE);
	const paginatedItems = filteredItems.slice(
		(currentPage - 1) * PAGE_SIZE,
		currentPage * PAGE_SIZE,
	);

	const getIconColor = (name: string) => {
		const colors = [
			'bg-indigo-600',
			'bg-blue-600',
			'bg-green-600',
			'bg-teal-600',
			'bg-cyan-600',
			'bg-red-600',
			'bg-pink-600',
			'bg-purple-600',
			'bg-yellow-600',
			'bg-orange-600',
		];

		let hash = 0;
		for (let i = 0; i < name.length; i++) {
			hash = name.charCodeAt(i) + ((hash << 5) - hash);
		}
		return colors[Math.abs(hash) % colors.length];
	};

	const tabItems = categories.map((tab) => ({
		key: tab,
		label: tab,
		children: (
			<TabItems
				paginatedItems={paginatedItems}
				getIconColor={getIconColor}
				currentPage={currentPage}
				totalPages={totalPages}
				totalItems={totalItems}
				PAGE_SIZE={PAGE_SIZE}
				onPageChange={setCurrentPage}
			/>
		),
	}));
	const loggedInUser = useAppSelector(getLoggedInUser);
	return (
		<div className="space-y-6">
			<Header
				title="App Integrations"
				description="Connect third-party apps to enhance your workflow"
				icon={<AppstoreOutlined />}
				debrand={loggedInUser?.resellerClient}
			/>
			<div className="flex justify-between items-center">
				<Button
					type="primary"
					className="text-base font-medium integrate-btn py-4 px-2"
					onClick={() => setIntegrationModalVisible(true)}
				>
					Request Integration
				</Button>
			</div>

			<SearchBar value={searchText} onChange={setSearchText} onClear={handleClearSearch} />

			{loading ? (
				<div className="flex justify-center py-10">
					<Spin size="large" tip="Loading templates..." />
				</div>
			) : (
				<div className="tabs-wrapper">
					{showLeftButton && (
						<button
							className="scroll-button scroll-button-left"
							onClick={scrollLeft}
							aria-label="Scroll left"
						>
							&#8249;
						</button>
					)}

					<div ref={tabsContainerRef} className="tabs-container">
						{categories.length > 0 ? (
							<Tabs
								defaultActiveKey={categories[0]}
								activeKey={activeTab}
								onChange={handleTabChange}
								className="custom-tabs"
								items={tabItems}
								tabBarGutter={8}
							/>
						) : (
							<div className="text-center py-5">No templates found</div>
						)}
					</div>

					{showRightButton && (
						<button
							className="scroll-button scroll-button-right"
							onClick={scrollRight}
							aria-label="Scroll right"
						>
							&#8250;
						</button>
					)}
				</div>
			)}
			<RequestIntegrationModal
				visible={integrationModalVisible}
				onClose={() => setIntegrationModalVisible(false)}
			/>
		</div>
	);
};

export default ApiWorkflowTemplate;
